/* eslint-disable max-lines-per-function */
/* eslint-disable max-statements */
import { Button, Center, Loader, Text, useMantineTheme } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconDownload, IconEye, IconTrash, IconUpload } from '@tabler/icons-react';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { CustomsError } from '@e-origin/shared';

import { getInitialCustomsErrorsCriteria, useAdminCustomsErrors, useAdminDeleteCustomsError, useCustomsErrorsCriteriaUrlSync } from '@admin/hooks';
import { CheckedColumn, SearchBox, Table, TableActions, TableColumnProps } from '@components';
import { ContentLayout } from '@features/layout';
import { EditCustomsError } from './EditCustomsError';
import { ExportCustomsErrorsModal } from './ExportCustomsErrorModal';
import { ImportCustomsErrorModal } from './ImportCustomsErrorModal';

export const ManageCustomsErrors = () => {
  const theme = useMantineTheme();
  const [searchParams] = useSearchParams();
  const initialCriteria = getInitialCustomsErrorsCriteria(searchParams);

  const [filters, setFilters] = useState(initialCriteria.filters);
  const [sorting, setSorting] = useState(initialCriteria.sorting);
  const [pagination, setPagination] = useState(initialCriteria.pagination);
  useCustomsErrorsCriteriaUrlSync({ filters, sorting, pagination });
  const { data: { data: { customsErrors = [], totalItems = 0 } = {} } = {}, isLoading } = useAdminCustomsErrors({
    filters,
    sorting,
    pagination,
  });
  const deleteCustomsErrorMutation = useAdminDeleteCustomsError();

  const handleSearch = (searchQuery: string) => {
    setFilters((prev) => ({
      ...prev,
      searchQuery,
    }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handlePagination = (page: number, size: number) => {
    setPagination({ page, size });
  };

  const handleSorting = (direction, field: string) => {
    setSorting({ field, direction });
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const openDeleteCustomsError = (customsError: CustomsError) => {
    modals.openConfirmModal({
      title: 'Delete Customs Error',
      children: <Text>Are you sure you want to delete this customs error?</Text>,
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        deleteCustomsErrorMutation.mutate(customsError._id);
      },
    });
  };

  const openCustomsErrorModal = (customsError?: CustomsError) => {
    modals.open({
      title: customsError ? 'Edit Customs Error' : 'New Customs Error',
      children: <EditCustomsError customsError={customsError} onHide={() => modals.closeAll()} />,
      size: 'lg',
    });
  };

  const openExportCustomsErrorModal = () => {
    modals.open({
      title: 'Export Customs Errors',
      children: <ExportCustomsErrorsModal onClose={() => modals.closeAll()} />,
      size: '800px',
    });
  };

  const openImportCustomsErrorModal = () => {
    modals.open({
      title: 'Import Customs Errors',
      children: <ImportCustomsErrorModal onClose={() => modals.closeAll()} />,
      size: 'lg',
    });
  };

  const columns: TableColumnProps<CustomsError>[] = [
    {
      name: 'Error Message',
      cell: (row) => row.errorMessage,
      sortable: true,
      selector: 'errorMessage',
    },
    {
      name: 'Error Reason',
      cell: (row) => row.errorReason,
      sortable: true,
      selector: 'errorReason',
    },
    {
      name: 'Declaration Attribute Pointer',
      cell: (row) => row.declarationAttributePointer || 'N/A',
      sortable: true,
      selector: 'declarationAttributePointer',
    },
    {
      name: 'Run Customs Sync By LRN',
      cell: (row) => <CheckedColumn checked={row.runCustomsSyncByLRN === true} />,
      sortable: true,
      selector: 'runCustomsSyncByLRN',
    },
    {
      name: 'Actions',
      sticky: 'right',
      cell: (row) => (
        <TableActions
          actions={[
            {
              iconComponent: <IconEye color={theme.colors[theme.primaryColor][6]} />,
              text: 'View Customs Error',
              onClick: () => openCustomsErrorModal(row),
            },
            {
              iconComponent: <IconTrash color={theme.colors[theme.primaryColor][6]} />,
              text: 'Delete',
              onClick: () => openDeleteCustomsError(row),
            },
          ]}
        />
      ),
    },
  ];

  if (isLoading) {
    return (
      <Center style={{ height: '200px' }}>
        <Loader size="xl" />
      </Center>
    );
  }

  return (
    <ContentLayout>
      <ContentLayout.Header>
        <ContentLayout.Title>Manage Customs Errors</ContentLayout.Title>
        <ContentLayout.Filters>
          <Button leftSection={<IconUpload size={16} />} onClick={() => openImportCustomsErrorModal()}>
            Import
          </Button>
          <Button leftSection={<IconDownload size={16} />} onClick={() => openExportCustomsErrorModal()}>
            Export
          </Button>
          <Button onClick={() => openCustomsErrorModal()} variant="outline" size="lg">
            Add new
          </Button>
          <SearchBox placeholder="Search by message, reason or attribute" handleSearch={handleSearch} value={filters?.searchQuery} />
        </ContentLayout.Filters>
      </ContentLayout.Header>
      <ContentLayout.Content>
        <Table
          columns={columns}
          dataSource={(customsErrors as any) || []}
          total={totalItems}
          onPaginationChange={handlePagination}
          onSortingChange={handleSorting}
          defaultPage={pagination.page}
          defaultPerPage={pagination.size}
          defaultSorting={sorting.field}
          defaultSortingDirection={sorting.direction}
          verticalSpacing="xs"
        />
      </ContentLayout.Content>
    </ContentLayout>
  );
};
